import { useEffect, useMemo, useRef, useState } from "react";

// Constants
const SCROLL_THRESHOLD = 1;
const END_SCROLL_THRESHOLD = 3;
const TABLE_BORDER_COLOR = "#d0d7de";
const TRANSITION_DELAY = 150;
const TABLE_SELECTOR = ".wmde-markdown table";

// Base styles
const DEFAULT_STYLES = {
  overflowX: "auto",
  maxWidth: "100%",
  transition: "width 0.2s ease-out",
} as const;

const DEFAULT_TABLE_STYLES = {
  maxWidth: "100%",
  borderLeft: "none",
  borderRight: "none",
  transition: "border-width 0.5s ease",
} as const;

// Types
interface UseRenderTablesProps {
  observedElementRef: React.RefObject<HTMLDivElement>;
}

interface TableScrollState {
  isScrolledLeft: boolean;
  isScrolledRight: boolean;
  index: number;
}

interface TableStyles {
  overflowX: string;
  maxWidth: string;
  transition: string;
  "&:nth-of-type(n)": {
    maxWidth: string;
    borderLeft: string;
    borderRight: string;
    transition: string;
  };
  [key: string]: any;
}

type ScrollListener = () => void;
type ScrollListenerMap = Map<HTMLElement, ScrollListener>;

export const useRenderTables = ({
  observedElementRef,
}: UseRenderTablesProps) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);
  const [tableScrollStates, setTableScrollStates] = useState<
    TableScrollState[]
  >([]);

  const previousWidth = useRef(0);
  const scrollListeners = useRef<ScrollListenerMap>(new Map());

  const handleResize = (
    newWidth: number,
    resizeTimeout: NodeJS.Timeout | undefined = undefined
  ) => {
    if (newWidth === previousWidth.current) return;

    previousWidth.current = newWidth;
    if (resizeTimeout) clearTimeout(resizeTimeout);

    setTableWidth(0);
    return setTimeout(() => {
      setContainerWidth(newWidth);
      setTableWidth(newWidth - 2);
    }, TRANSITION_DELAY);
  };

  const isTableScrollable = (table: HTMLElement) => {
    return table.scrollWidth > containerWidth && containerWidth > 0;
  };

  const updateTableScrollBorders = (table: HTMLElement, tableIndex: number) => {
    if (!isTableScrollable(table)) {
      setTableScrollStates((states) =>
        // Remove the table from scroll states if not scrollable
        states.filter((state) => state.index !== tableIndex)
      );
      return;
    }

    const hasScrolledFromStart = table.scrollLeft > SCROLL_THRESHOLD;
    const hasReachedEnd =
      Math.abs(table.scrollLeft + containerWidth - table.scrollWidth) <=
      END_SCROLL_THRESHOLD;

    const newState = {
      index: tableIndex,
      isScrolledLeft: hasScrolledFromStart,
      isScrolledRight: !hasReachedEnd,
    };

    setTableScrollStates((states) => [
      ...states.filter((state) => state.index !== tableIndex),
      newState,
    ]);
  };

  // Setup resize observer
  useEffect(() => {
    if (!observedElementRef.current) return;

    let resizeTimeout: NodeJS.Timeout | undefined;
    const observer = new ResizeObserver((entries) => {
      const newTimeout = handleResize(
        entries[0].contentRect.width,
        resizeTimeout
      );
      if (newTimeout) resizeTimeout = newTimeout;
    });

    observer.observe(observedElementRef.current);
    return () => {
      observer.disconnect();
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [observedElementRef.current]);

  // Setup scroll handlers
  useEffect(() => {
    if (!observedElementRef.current || !containerWidth) return;

    const clearScrollListeners = () => {
      scrollListeners.current.forEach((listener, table) => {
        table.removeEventListener("scroll", listener);
      });
      scrollListeners.current.clear();
    };

    clearScrollListeners();

    const tables = observedElementRef.current.querySelectorAll(TABLE_SELECTOR);
    tables.forEach((table, index) => {
      if (table instanceof HTMLElement) {
        // Set initial scroll state
        updateTableScrollBorders(table, index);
        // Create and store scroll listener
        const listener = () => updateTableScrollBorders(table, index);
        scrollListeners.current.set(table, listener);
        table.addEventListener("scroll", listener);
      }
    });

    return clearScrollListeners;
  }, [observedElementRef.current, containerWidth]);

  const getTableBorderStyle = (isScrolled: boolean) =>
    isScrolled ? `1px solid ${TABLE_BORDER_COLOR}` : "none";

  const tableStyles: TableStyles = useMemo(
    () => ({
      ...DEFAULT_STYLES,
      "&:nth-of-type(n)": {
        ...DEFAULT_TABLE_STYLES,
        maxWidth: `${containerWidth}px`,
      },
      ...tableScrollStates.reduce(
        (acc, { index, isScrolledLeft, isScrolledRight }) => ({
          ...acc,
          [`&:nth-of-type(${index + 1})`]: {
            width: tableWidth ? `${tableWidth}px` : "0px",
            borderLeft: getTableBorderStyle(isScrolledLeft),
            borderRight: getTableBorderStyle(isScrolledRight),
          },
        }),
        {}
      ),
    }),
    [containerWidth, tableWidth, tableScrollStates]
  );

  return { tableStyles, docWidth: containerWidth };
};
